// src/components/school/StudentListLayer.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { 
  FaSearch, 
  FaChevronLeft, 
  FaChevronRight, 
  FaPhoneAlt, 
  FaQrcode,
  FaCheckCircle
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getActivationRequests, updateIdCardStatus } from "../../services/studentService";
import "./StudentListLayer.css";

const StudentListLayer = () => {
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("All");
  const [schoolFilter, setSchoolFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedIds, setSelectedIds] = useState([]);
  const [cardCodes, setCardCodes] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");



  /* ================= FETCH DATA ================= */
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (schoolFilter !== "All") params.school_id = schoolFilter;
      if (classFilter !== "All") params.class_id = classFilter;
      if (search.trim()) params.search = search.trim();
      
      if (statusFilter === "active") {
        params.student_account_status = "active";
      } else if (statusFilter === "requested") {
        params.id_card_status = "requested";
      } else if (statusFilter !== "All") {
        params.id_card_status = statusFilter;
      }

      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const res = await getActivationRequests(params);
      if (res?.data?.status) {
        const list = res.data.data.requests || [];
        setRequests(list);

        // Extract unique schools and classes only once during initial full load
        if (schoolFilter === "All" && classFilter === "All" && statusFilter === "All" && !search && !fromDate && !toDate) {
          const uniqueSchools = [];
          const schoolIds = new Set();
          list.forEach(r => {
            if (r.school_id && !schoolIds.has(r.school_id)) {
              schoolIds.add(r.school_id);
              uniqueSchools.push({ id: r.school_id, name: r.school_name });
            }
          });
          setSchools(uniqueSchools);

          const uniqueClasses = [];
          const classIds = new Set();
          list.forEach(r => {
            if (r.class_id && !classIds.has(r.class_id)) {
              classIds.add(r.class_id);
              uniqueClasses.push({ id: r.class_id, name: r.class_name });
            }
          });
          setClasses(uniqueClasses);
        }
      } else {
        toast.error(res?.data?.message || "Failed to load activation requests");
        setRequests([]);
      }
    } catch (error) {
      toast.error("Something went wrong while fetching data");
      setRequests([]);
    }
    setLoading(false);
  }, [search, classFilter, schoolFilter, statusFilter, fromDate, toDate]);

  // Triggers API query whenever search, filter dropdowns, or dates change
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  /* ================= HELPERS ================= */
  const statuses = ["All", "active", "requested"];

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(displayed.map((r) => r.request_id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleCardCodeChange = (id, val) => {
    setCardCodes((prev) => ({
      ...prev,
      [id]: val
    }));
  };



  /* ================= ACTIVATE STATUS FLOW ================= */
  const handleActivateSingle = async (r) => {
    const code = cardCodes[r.request_id] !== undefined ? cardCodes[r.request_id] : r.id_card_code;
    if (!code || !code.trim()) {
      toast.error("Please enter a valid ID card code first");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        students: [
          {
            student_id: r.student_qr_code,
            id_card_code: code.trim()
          }
        ]
      };

      const res = await updateIdCardStatus(payload);
      if (res?.data?.status) {
        const resData = res.data.data || {};
        const failedRecords = resData.failed_records || [];

        if (failedRecords.length === 0) {
          toast.success(res.message || res.data?.message || "ID Card updated successfully");
          // Instantly update requests state in React memory
          setRequests((prev) => 
            prev.map(item => 
              item.request_id === r.request_id 
                ? { ...item, id_card_code: code.trim(), id_card_status: "generated" }
                : item
            )
          );
        } else if (failedRecords.length > 0) {
          toast.error(failedRecords[0].reason || "Failed to update ID card status");
        } else {
          toast.error(res.message || res.data?.message || "Failed to update ID card status");
        }

        // Clear local input for this request
        setCardCodes((prev) => {
          const updated = { ...prev };
          delete updated[r.request_id];
          return updated;
        });
        fetchRequests(); // Refresh list from server
      } else {
        toast.error(res?.message || res?.data?.message || "Failed to update ID card status");
      }
    } catch (error) {
      toast.error("An error occurred during activation");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateSelected = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select student requests first");
      return;
    }

    const studentsPayload = [];
    for (const reqId of selectedIds) {
      const r = requests.find((x) => x.request_id === reqId);
      if (r) {
        const code = cardCodes[r.request_id] !== undefined ? cardCodes[r.request_id] : r.id_card_code;
        if (!code || !code.trim()) {
          toast.error(`Please enter an ID card code for student ${r.student_name}`);
          return;
        }
        studentsPayload.push({
          student_id: r.student_qr_code,
          id_card_code: code.trim()
        });
      }
    }

    setLoading(true);
    try {
      const res = await updateIdCardStatus({ students: studentsPayload });
      if (res?.data?.status) {
        const resData = res.data.data || {};
        const failedRecords = resData.failed_records || [];
        const successCount = studentsPayload.length - failedRecords.length;

        // Instantly update requests state in React memory
        if (successCount > 0) {
          setRequests((prev) => 
            prev.map(item => {
              const matched = studentsPayload.find(p => p.student_id === item.student_qr_code);
              const isFailed = failedRecords.some(f => f.data_provided.student_id === item.student_qr_code);
              if (matched && !isFailed) {
                return { ...item, id_card_code: matched.id_card_code, id_card_status: "generated" };
              }
              return item;
            })
          );
        }

        if (failedRecords.length === 0) {
          toast.success(res.message || res.data?.message || "Selected ID Cards activated successfully");
        } else {
          if (successCount > 0) {
            toast.warning(`Processed: ${successCount} successful, ${failedRecords.length} failed`);
          }
          failedRecords.forEach(f => {
            toast.error(`Failed: ${f.reason}`);
          });
        }

        setSelectedIds([]);
        setCardCodes({});
        fetchRequests();
      } else {
        toast.error(res?.message || res?.data?.message || "Failed to activate selected ID cards");
      }
    } catch (error) {
      toast.error("An error occurred during bulk activation");
    } finally {
      setLoading(false);
    }
  };

  /* ================= FILTERING ================= */
  const filtered = requests;

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, classFilter, schoolFilter, statusFilter, fromDate, toDate]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const displayed = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  return (
    <div className="student-wrapper">
      <div className="student-card">
        
        {/* HEADER */}
        <div className="list-header">
          <div>
            <h5 className="fw-bold text-dark">Student ID Card Active Requests</h5>
            <p className="text-secondary-light mb-0">Total Activation Requests: <strong>{filtered.length}</strong></p>
          </div>
          <div className="header-actions">
            <span className="badge bg-primary-subtle text-primary-600 px-3 py-2 rounded-pill fw-semibold">Superadmin View</span>
          </div>
        </div>

        {/* CONTROLS */}
        <div className="list-controls d-flex flex-column gap-3 p-3 bg-light" style={{ borderRadius: "12px" }}>
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between w-100">
            
            {/* Search Box */}
            <div className="search-box d-flex align-items-center border px-3 py-2 bg-white flex-grow-1" style={{ borderRadius: "8px", maxWidth: "400px" }}>
              <FaSearch className="text-muted me-2" />
              <input
                type="text"
                className="border-0 w-100"
                placeholder="Search student, roll no or school..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ outline: "none" }}
              />
            </div>

            {/* Bulk actions */}
            <div className="bulk-actions d-flex gap-2">
              <button 
                className="btn btn-success d-flex align-items-center gap-2 px-3 py-2" 
                onClick={handleActivateSelected}
                style={{ borderRadius: "8px", fontSize: "14px" }}
              >
                <FaCheckCircle /> Activate Selected ({selectedIds.length})
              </button>
            </div>

          </div>

          <div className="d-flex flex-wrap gap-3 align-items-center w-100 border-top pt-2">
            
            {/* Page Size Filter */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small fw-semibold">Show:</span>
              <select 
                className="form-select form-select-sm border" 
                value={pageSize === 999999 ? "All" : pageSize} 
                onChange={(e) => {
                  const val = e.target.value;
                  setPageSize(val === "All" ? 999999 : Number(val));
                }}
                style={{ borderRadius: "8px", minWidth: "80px" }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={100}>100</option>
                <option value="All">All</option>
              </select>
            </div>

            {/* School Filter */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small fw-semibold">School:</span>
              <select 
                className="form-select form-select-sm border" 
                value={schoolFilter} 
                onChange={(e) => setSchoolFilter(e.target.value)}
                style={{ borderRadius: "8px", minWidth: "150px" }}
              >
                <option value="All">All Schools</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* Class Filter */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small fw-semibold">Class:</span>
              <select 
                className="form-select form-select-sm border" 
                value={classFilter} 
                onChange={(e) => setClassFilter(e.target.value)}
                style={{ borderRadius: "8px", minWidth: "120px" }}
              >
                <option value="All">All Classes</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* Status Filter */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small fw-semibold">Status:</span>
              <select 
                className="form-select form-select-sm border" 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ borderRadius: "8px", minWidth: "120px" }}
              >
                {statuses.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>

            {/* Date Range Filters */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small fw-semibold">From:</span>
              <input 
                type="date" 
                className="form-control form-control-sm border" 
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{ borderRadius: "8px", width: "130px" }}
              />
            </div>

            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small fw-semibold">To:</span>
              <input 
                type="date" 
                className="form-control form-control-sm border" 
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{ borderRadius: "8px", width: "130px" }}
              />
            </div>

          </div>
        </div>

        {/* TABLE */}
        <div className="table-responsive mt-3" style={{ borderRadius: "12px", border: "1px solid #E2E8F0" }}>
          <table className="table table-hover align-middle mb-0" style={{ fontSize: "14px" }}>
            <thead className="table-light">
              <tr>
                <th className="ps-4">
                  <input 
                    type="checkbox" 
                    className="form-check-input"
                    onChange={handleSelectAll} 
                    checked={displayed.length > 0 && selectedIds.length === displayed.length}
                  />
                </th>
                <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Student Details</th>
                <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>School Details</th>
                <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Class</th>
                <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Roll No</th>
                <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>QR Code</th>
                <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>ID Card Code</th>
                <th className="py-3 text-muted fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Status</th>
                <th className="py-3 text-muted fw-bold text-uppercase text-center" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-5">
                    <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                    Processing requests...
                  </td>
                </tr>
              ) : displayed.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-5 fw-medium">
                    No activation requests found
                  </td>
                </tr>
              ) : (
                displayed.map((r) => {
                  const isMale = r.student_gender === "Male";
                  return (
                    <tr key={r.request_id} style={{ transition: "all 0.15s ease" }}>
                      <td className="ps-4">
                        <input 
                          type="checkbox" 
                          className="form-check-input"
                          checked={selectedIds.includes(r.request_id)} 
                          onChange={() => handleSelectOne(r.request_id)}
                        />
                      </td>
                      <td className="py-3">
                        <div className="d-flex align-items-center gap-3">
                          {r.student_image || r.image || r.student_photo || r.photo ? (
                            <img 
                              src={r.student_image || r.image || r.student_photo || r.photo} 
                              alt="Student" 
                              className="rounded-circle shadow-sm" 
                              style={{ width: "36px", height: "36px", objectFit: "cover", flexShrink: 0 }}
                            />
                          ) : (
                            <div 
                              className="d-flex align-items-center justify-content-center text-white fw-bold rounded-circle shadow-sm"
                              style={{ 
                                width: "36px", 
                                height: "36px", 
                                background: isMale ? "linear-gradient(135deg, #3B82F6, #1D4ED8)" : "linear-gradient(135deg, #EC4899, #BE185D)",
                                fontSize: "14px",
                                flexShrink: 0
                              }}
                            >
                              {r.student_name ? r.student_name.charAt(0).toUpperCase() : "S"}
                            </div>
                          )}
                          <div>
                            <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{r.student_name}</div>
                            <div className="text-muted small" style={{ fontSize: "11px" }}>{r.student_email}</div>
                            <div className="text-secondary small" style={{ fontSize: "11px" }}><FaPhoneAlt size={10} className="me-1" />{r.student_mobile}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{r.school_name}</div>
                        <div className="text-muted small" style={{ fontSize: "11px" }}>{r.school_email}</div>
                        <div className="text-secondary small" style={{ fontSize: "11px" }}>{r.school_phone}</div>
                      </td>
                      <td className="py-3 fw-semibold text-secondary">{r.class_name || "N/A"}</td>
                      <td className="py-3 text-secondary-light fw-medium">{r.student_roll_number || "N/A"}</td>
                      <td className="py-3">
                        {r.student_qr_code ? (
                          <div className="d-flex align-items-center gap-1">
                            <FaQrcode size={12} className="text-muted" />
                            <code className="text-secondary small">{r.student_qr_code}</code>
                          </div>
                        ) : (
                          <span className="text-muted small">N/A</span>
                        )}
                      </td>
                      <td className="py-3">
                        <input 
                          type="text" 
                          className="form-control form-control-sm border" 
                          placeholder="Enter ID code..."
                          value={cardCodes[r.request_id] !== undefined ? cardCodes[r.request_id] : (r.id_card_code || "")}
                          onChange={(e) => handleCardCodeChange(r.request_id, e.target.value)}
                          style={{ borderRadius: "8px", maxWidth: "160px", fontSize: "13px", padding: "6px 12px", background: "#FCFDFE" }}
                          disabled={r.id_card_status === "printed"}
                        />
                      </td>
                      <td className="py-3">
                        <span
                          className="badge d-inline-flex align-items-center gap-1.5 px-2.5 py-1.5 fw-semibold"
                          style={{
                            borderRadius: "30px",
                            backgroundColor:
                              r.id_card_status === "requested"
                                ? "#FEF3C7"
                                : r.id_card_status === "generated"
                                ? "#DBEAFE"
                                : r.id_card_status === "printed"
                                ? "#DCFCE7"
                                : "#F3F4F6",
                            color:
                              r.id_card_status === "requested"
                                ? "#D97706"
                                : r.id_card_status === "generated"
                                ? "#2563EB"
                                : r.id_card_status === "printed"
                                ? "#15803D"
                                : "#4B5563",
                            fontSize: "12px",
                            border: `1px solid ${
                              r.id_card_status === "requested"
                                ? "#fcd34d"
                                : r.id_card_status === "generated"
                                ? "#bfdbfe"
                                : r.id_card_status === "printed"
                                ? "#bbf7d0"
                                : "#e5e7eb"
                            }`
                          }}
                        >
                          <span 
                            style={{ 
                              width: "6px", 
                              height: "6px", 
                              borderRadius: "50%", 
                              backgroundColor:
                                r.id_card_status === "requested"
                                  ? "#F59E0B"
                                  : r.id_card_status === "generated"
                                  ? "#3B82F6"
                                  : r.id_card_status === "printed"
                                  ? "#10B981"
                                  : "#6B7280",
                              display: "inline-block" 
                            }} 
                          />
                          {r.id_card_status || "Requested"}
                        </span>
                      </td>
                      <td className="py-3 text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <button 
                            type="button"
                            className="btn btn-sm px-2.5 py-1.5 d-flex align-items-center gap-1 text-white"
                            onClick={() => handleActivateSingle(r)} 
                            title={r.id_card_code ? "Update ID Card" : "Save & Activate Card"}
                            style={{ 
                              borderRadius: "6px", 
                              fontSize: "12px", 
                              border: "none", 
                              backgroundColor: "#2563eb", // Blue background color
                              fontWeight: "600"
                            }}
                            disabled={r.id_card_status === "printed"}
                          >
                            <FaCheckCircle /> {r.id_card_code ? "Update" : "Activate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="pagination px-4 py-3 border-top border-light-subtle d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 bg-light-subtle" style={{ borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px" }}>
            <span className="text-secondary-light small">
              Showing <strong>{displayed.length}</strong> of <strong>{filtered.length}</strong> requests
            </span>
            <div className="d-flex gap-1">
              <button 
                disabled={page === 1} 
                onClick={() => setPage(page - 1)} 
                className="btn btn-sm btn-white border px-3"
                style={{ background: "#fff", borderRadius: "8px" }}
              >
                <FaChevronLeft size={10} className="me-1" /> Prev
              </button>
              
              <div className="align-items-center px-3 text-muted small d-flex">
                Page <strong>{page}</strong> of {totalPages}
              </div>

              <button 
                disabled={page === totalPages} 
                onClick={() => setPage(page + 1)} 
                className="btn btn-sm btn-white border px-3"
                style={{ background: "#fff", borderRadius: "8px" }}
              >
                Next <FaChevronRight size={10} className="ms-1" />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* RESULT MODAL REMOVED */}

    </div>
  );
};

export default StudentListLayer;
